/* eslint-disable lingui/text-restrictions */

import { t } from "@lingui/macro";
import {
  ArrowRight,
  CircleNotch,
  Copy,
  DownloadSimple,
  FileDoc,
  FilePdf,
  FileText,
  X,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import _axios from "axios";
import { saveAs } from "file-saver";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useState } from "react";
import { create } from "zustand";

import { useFeatureGate } from "@/client/hooks/use-feature-gate";
import { useTakingLonger } from "@/client/hooks/use-taking-longer";
import { useToast } from "@/client/hooks/use-toast";
import { processAi, reportAiOutcome } from "@/client/services/ai";
import { useResumeStore } from "@/client/stores/resume";

interface CoverLetterState {
  open: boolean;
  openDialog: () => void;
  closeDialog: () => void;
}

export const useCoverLetterStore = create<CoverLetterState>()((set) => ({
  open: false,
  openDialog: () => set({ open: true }),
  closeDialog: () => set({ open: false }),
}));

export const CoverLetterDialog = () => {
  const { open, closeDialog } = useCoverLetterStore();
  const { toast } = useToast();
  const { gate } = useFeatureGate();
  const [jobDescription, setJobDescription] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const takingLonger = useTakingLonger(loading);
  const [result, setResult] = useState("");

  const handleGenerate = useCallback(async () => {
    if (!gate("cover-letter")) return;

    if (!jobDescription.trim()) {
      toast({
        variant: "info",
        title: t`Job description required`,
        description: t`Paste the job description to generate a tailored cover letter.`,
      });
      return;
    }

    const { resume } = useResumeStore.getState();

    setLoading(true);
    try {
      const response = await processAi({
        action: "generate-section",
        sectionType: "cover-letter",
        jobDescription,
        context: {
          resumeData: resume.data,
          companyName,
        },
      });

      if (response.ok === false) {
        toast({
          variant: "error",
          title: t`Please try again`,
          description: response.result,
        });
        return;
      }

      setResult(response.result);
      reportAiOutcome(response.callId, "used");
    } catch {
      toast({
        variant: "error",
        title: t`Failed to generate cover letter`,
        description: t`Please try again.`,
      });
    } finally {
      setLoading(false);
    }
  }, [gate, jobDescription, companyName, toast]);

  const handleCopy = useCallback(() => {
    const plainText = result.replace(/<[^>]*>/g, "").trim();
    navigator.clipboard.writeText(plainText);
    toast({
      variant: "success",
      title: t`Copied to clipboard`,
    });
  }, [result, toast]);

  const plainLetter = result.replace(/<[^>]*>/g, "").trim();

  const handleDownloadTxt = useCallback(() => {
    saveAs(new Blob([plainLetter], { type: "text/plain;charset=utf-8" }), "cover-letter.txt");
  }, [plainLetter]);

  const handlePrintPdf = useCallback(() => {
    const escaped = plainLetter
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(
      `<!doctype html><html><head><title>Cover letter</title><style>
        body { font-family: Georgia, serif; max-width: 720px; margin: 48px auto; line-height: 1.5; white-space: pre-wrap; }
        @media print { body { margin: 24px; } }
      </style></head><body>${escaped}</body></html>`,
    );
    win.document.close();
    win.focus();
    win.print();
  }, [plainLetter]);

  const handleDownloadDocx = useCallback(async () => {
    try {
      const raw = _axios.create({ baseURL: "/api", withCredentials: true });
      const response = await raw.post("/ai/letter-docx", { text: plainLetter }, { responseType: "blob" });
      saveAs(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }),
        "cover-letter.docx",
      );
    } catch {
      toast({
        variant: "error",
        title: t`Failed to download Word file`,
        description: t`Please try again.`,
      });
    }
  }, [plainLetter, toast]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeDialog} />

      <div className="relative z-10 mx-4 flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-2">
            <FileText size={20} weight="duotone" className="text-primary" />
            <h2 className="text-lg font-semibold">{t`AI Cover Letter Generator`}</h2>
          </div>
          <button
            onClick={closeDialog}
            className="rounded-full p-1 text-foreground/40 hover:text-foreground"
            aria-label={t`Close`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!result ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-foreground/70">
                  {t`Company Name (optional)`}
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t`e.g. Google, Acme Corp...`}
                  className="w-full rounded-lg border border-border/50 bg-transparent px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-foreground/70">
                  {t`Job Description *`}
                </label>
                <textarea
                  value={jobDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJobDescription(e.target.value)}
                  placeholder={t`Paste the full job description here...`}
                  className="min-h-[200px] w-full rounded-lg border border-border/50 bg-transparent px-3 py-2 text-sm outline-none transition-colors focus:border-primary/50"
                />
              </div>

              <p className="text-[11px] text-foreground/40">
                {t`The AI will use your current resume and the job description. It will not invent jobs or metrics.`}
              </p>

              <Button
                onClick={handleGenerate}
                disabled={loading || !jobDescription.trim()}
                className="w-full"
              >
                {loading ? (
                  <>
                    <CircleNotch size={16} className="mr-2 animate-spin" />
                    {takingLonger ? t`Taking longer than usual…` : t`Generating...`}
                  </>
                ) : (
                  <>
                    {t`Generate Cover Letter`}
                    <ArrowRight size={16} className="ml-2" weight="bold" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="rounded-lg border border-border/30 bg-secondary-accent/10 p-4">
                  <div
                    className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/80 [&>p]:mb-4 [&>p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: result.replace(/<(?!\/?(p|br)\b)[^>]*>/gi, "") }}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleCopy} variant="outline" className="flex-1">
                    <Copy size={14} className="mr-1.5" />
                    {t`Copy to Clipboard`}
                  </Button>
                  <Button onClick={handleDownloadTxt} variant="outline" className="flex-1">
                    <DownloadSimple size={14} className="mr-1.5" />
                    {t`Text file`}
                  </Button>
                  <Button onClick={handlePrintPdf} variant="outline" className="flex-1">
                    <FilePdf size={14} className="mr-1.5" />
                    {t`Print / PDF`}
                  </Button>
                  <Button onClick={handleDownloadDocx} variant="outline" className="flex-1">
                    <FileDoc size={14} className="mr-1.5" />
                    {t`Word`}
                  </Button>
                  <Button
                    onClick={() => { setResult(""); }}
                    variant="ghost"
                    className="flex-1"
                  >
                    {t`Generate Another`}
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};
